import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import stylecycleLogo from "@/assets/stylecycle-logo.avif";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { m, AnimatePresence } from "framer-motion";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  { label: "Products", path: "/products" },
  { label: "Services", path: "/services" },
  { label: "Certifications", path: "/network" },
  { label: "Our Factories", path: "/factories" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    let lastScrolled = scrolled;
    const update = () => {
      const next = window.scrollY > 50;
      if (next !== lastScrolled) {
        lastScrolled = next;
        setScrolled(next);
      }
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const textColor = scrolled ? "text-charcoal" : "text-porcelain";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4 sm:px-6 lg:px-8 transition-all duration-500"
      style={{ paddingTop: scrolled ? '0.5rem' : '1rem' }}
    >
      <nav className={`w-[250px] sm:w-[350px] lg:w-full max-w-6xl flex items-center justify-between h-14 md:h-16 px-3 sm:px-4 md:px-6 lg:px-8 rounded-2xl transition-all duration-500 ${
        scrolled
          ? 'bg-porcelain/85 backdrop-blur-xl shadow-[0_8px_32px_-8px_rgba(0,0,0,0.12)] border border-border/60'
          : 'bg-transparent border border-transparent shadow-none'
      }`}>
        <Link to="/" className={`flex items-center gap-2 font-heading text-2xl font-bold tracking-tight transition-colors duration-300 ${textColor}`}>
          <img src={stylecycleLogo} alt="StyleCycle" className="h-8 md:h-9 w-auto" />
          <span className="font-heading">StyleCycle</span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`relative text-sm font-medium tracking-wide px-3.5 py-1.5 rounded-lg transition-colors duration-200 group ${
                location.pathname === link.path
                  ? 'text-soft-moss'
                  : `${textColor} hover:text-soft-moss`
              }`}
            >
              {link.label}
              <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-[2px] bg-soft-moss rounded-full transition-all duration-300 ${
                location.pathname === link.path ? 'w-4' : 'w-0 group-hover:w-4'
              }`} />
            </Link>
          ))}
        </div>

        <div className="hidden lg:block">
          <Link to="/contact">
            <Button
              className="bg-soft-moss hover:bg-sage-green text-porcelain font-medium px-6 rounded-full text-sm transition-all duration-300 hover:shadow-lg hover:shadow-soft-moss/20 hover:-translate-y-0.5"
            >
              Contact Us
            </Button>
          </Link>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden p-2 rounded-lg transition-all duration-200 ${textColor} hover:bg-soft-moss/10`}
          aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={mobileOpen}
        >

          <m.div
            animate={{ rotate: mobileOpen ? 90 : 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </m.div>
        </button>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <m.div
            className="lg:hidden absolute top-full left-4 right-4 sm:left-6 sm:right-6 lg:left-8 lg:right-8 mt-2 max-w-6xl mx-auto bg-porcelain/90 backdrop-blur-xl rounded-2xl border border-border/60 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.15)] overflow-hidden"
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <div className="p-5 flex flex-col gap-1">
              {navLinks.map((link, i) => (
                <m.div
                  key={link.path}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04, duration: 0.2 }}
                >
                  <Link
                    to={link.path}
                    className={`text-base font-medium py-2.5 px-3 block rounded-lg transition-all duration-200 ${
                      location.pathname === link.path
                        ? 'text-soft-moss'
                        : 'text-charcoal hover:text-soft-moss'
                    }`}
                  >
                    {link.label}
                  </Link>
                </m.div>
              ))}
              <m.div
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navLinks.length * 0.04, duration: 0.2 }}
                className="mt-2"
              >
                <Link to="/contact" className="block">
                  <Button className="w-full bg-soft-moss hover:bg-sage-green text-porcelain rounded-full">
                    Contact Us
                  </Button>
                </Link>
              </m.div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
